"use client";

import React, { useRef, useState, useEffect } from "react";
import { 
  Sparkles, 
  ShieldCheck, 
  CheckCircle2, 
  Activity, 
  Microscope, 
  Stethoscope, 
  Clock, 
  Award, 
  HeartHandshake, 
  Check, 
  Zap, 
  Flame, 
  Target,
  ChevronLeft,
  ChevronRight,
  ArrowRight
} from "lucide-react";
import { ProcedureSectionData } from "@/lib/procedure-sections/types";
import { SafeHtml } from "@/components/shared/SafeHtml";

const ICONS = [
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  Activity,
  Microscope,
  Stethoscope,
  Clock,
  Award,
  HeartHandshake,
  Zap,
  Flame,
  Target,
];

function ColoredTitle({ text }: { text?: string }) {
  if (!text) return null;
  const words = text.split(" ");
  if (words.length <= 1) return <span>{text}</span>;
  
  let orangeCount = 1;
  if (words.length >= 6) orangeCount = 3;
  else if (words.length >= 4) orangeCount = 2;

  const firstPart = words.slice(0, words.length - orangeCount).join(" ");
  const lastPart = words.slice(words.length - orangeCount).join(" ");

  return (
    <>
      <span className="text-brand-black">{firstPart} </span>
      <span className="text-brand-orange">{lastPart}</span>
    </>
  );
}

export function CardsGridSection({ section, index }: { section: ProcedureSectionData; index: number }) {
  const variant = section.layoutVariant || "grid_modern";
  const columns = section.columns || 3;
  const gridColClass = 
    columns === 2 ? "sm:grid-cols-2" : 
    columns === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : 
    "sm:grid-cols-2 lg:grid-cols-3";

  // Carousel slider state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
      const index = Math.round(scrollLeft / (clientWidth * 0.8 || 1));
      setActiveSlideIndex(index);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [section.items]);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === "left" ? -380 : 380;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      setTimeout(checkScroll, 350);
    }
  };

  return (
    <div className="w-full">
      {/* Section Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-10">
        <div className="max-w-3xl">
          {section.badge && (
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-peach/40 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-brand-orange-deep">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-orange animate-pulse" />
              <span>{section.badge}</span>
            </div>
          )}
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black leading-tight">
            <ColoredTitle text={section.title} />
          </h2>
          {section.subtitle && (
            <p className="mt-3 text-lg text-brand-gray leading-relaxed">{section.subtitle}</p>
          )}
          <div className="mt-4 h-1 w-24 rounded-full bg-brand-orange" />
        </div>

        {/* Carousel Navigation Controls */}
        {variant === "grid_carousel" && section.items && section.items.length > 0 && (
          <div className="flex items-center gap-3 self-start md:self-end">
            <span className="text-xs font-semibold text-brand-gray mr-2">
              Slide {Math.min(activeSlideIndex + 1, section.items.length)} of {section.items.length}
            </span>
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-gray/20 bg-white text-brand-black shadow-sm transition-all hover:bg-brand-orange hover:text-white hover:border-brand-orange disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Previous Slide"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-gray/20 bg-white text-brand-black shadow-sm transition-all hover:bg-brand-orange hover:text-white hover:border-brand-orange disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Next Slide"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* Intro Rich Text */}
      {section.contentHtml && (
        <div className="mb-6 md:mb-10 prose prose-lg max-w-none text-brand-gray font-normal leading-relaxed text-base sm:text-lg cms-content">
          <SafeHtml html={section.contentHtml} />
        </div>
      )}

      {/* Items Rendering Based on Variant */}
      {section.items && section.items.length > 0 && (
        <>
          {/* 1. ASYMMETRICAL MODERN BENTO GRID VARIANT */}
          {variant === "grid_hex_bento" && (
            <div className="my-8 md:my-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {section.items.map((item, itemIdx) => {
                const isFeatured = itemIdx === 0;
                const IconComp = ICONS[itemIdx % ICONS.length];
                return (
                  <div
                    key={itemIdx}
                    className={`group relative overflow-hidden rounded-3xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                      isFeatured
                        ? "md:col-span-2 md:row-span-2 bg-gradient-to-br from-[#FFF8F3] via-white to-brand-peach/25 border-brand-orange/40 p-8 sm:p-12 shadow-lg flex flex-col justify-between"
                        : "bg-white border-brand-gray/15 p-6 sm:p-7 shadow-sm hover:border-brand-orange/40 flex flex-col justify-between"
                    }`}
                  >
                    {/* Background Ambient Glow for featured tile */}
                    {isFeatured && (
                      <div className="absolute top-0 right-0 h-64 w-64 bg-brand-orange/10 rounded-full blur-3xl pointer-events-none" />
                    )}

                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-5">
                        <div className={`flex items-center justify-center rounded-2xl ${
                          isFeatured 
                            ? "h-14 w-14 bg-brand-orange text-white shadow-lg shadow-brand-orange/30" 
                            : "h-11 w-11 bg-brand-peach/40 text-brand-orange"
                        }`}>
                          <IconComp className={isFeatured ? "h-7 w-7" : "h-5 w-5"} />
                        </div>
                        <span className={`text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 ${
                          isFeatured 
                            ? "bg-brand-orange text-white shadow-xs" 
                            : "bg-brand-peach/30 text-brand-orange-deep"
                        }`}>
                          {isFeatured ? "Primary Focus • 01" : `Highlight 0${itemIdx + 1}`}
                        </span>
                      </div>
                      <h3 className={`font-bold text-brand-black mb-3 group-hover:text-brand-orange-deep transition-colors ${
                        isFeatured ? "text-2xl sm:text-3xl lg:text-4xl leading-snug" : "text-xl leading-tight"
                      }`}>
                        {item.title}
                      </h3>
                      {item.description && (
                        <div
                          className={`text-brand-gray leading-relaxed ${isFeatured ? "text-base sm:text-lg" : "text-sm sm:text-base"}`}
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      )}
                    </div>

                    {isFeatured && (
                      <div className="relative z-10 mt-8 pt-6 border-t border-brand-orange/15 flex items-center gap-2 text-xs font-bold text-brand-orange-deep uppercase tracking-wider">
                        <CheckCircle2 className="h-4 w-4 text-brand-orange" />
                        <span>Clinically Certified Protocol &bull; Dermalife Standard</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* 2. FROSTED GLASSMORPHISM VARIANT */}
          {variant === "grid_glass" && (
            <div className={`my-8 md:my-10 grid grid-cols-1 gap-6 ${gridColClass}`}>
              {section.items.map((item, itemIdx) => {
                const IconComp = ICONS[itemIdx % ICONS.length];
                return (
                  <div
                    key={itemIdx}
                    className="group relative overflow-hidden rounded-3xl border border-white/80 bg-white/75 backdrop-blur-xl p-8 shadow-[0_10px_35px_rgba(0,0,0,0.05)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_45px_rgba(245,130,32,0.15)] hover:border-brand-orange/40 flex flex-col justify-between"
                  >
                    {/* Top glass gradient pill */}
                    <div className="absolute top-0 right-0 h-28 w-28 rounded-full bg-gradient-to-br from-brand-orange/15 to-transparent blur-xl pointer-events-none transition-transform group-hover:scale-150" />
                    
                    <div>
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex h-13 w-13 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-orange to-brand-orange-deep text-white shadow-md shadow-brand-orange/25 group-hover:scale-105 transition-transform">
                          <IconComp className="h-6 w-6" />
                        </div>
                        <span className="font-mono text-xs font-bold text-brand-orange-deep bg-white/90 border border-brand-orange/20 px-3 py-1 rounded-full shadow-2xs">
                          0{itemIdx + 1}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-brand-black mb-3 group-hover:text-brand-orange-deep transition-colors">
                        {item.title}
                      </h3>
                      {item.description && (
                        <div 
                          className="text-sm sm:text-base text-brand-gray leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      )}
                    </div>

                    <div className="mt-6 pt-4 border-t border-brand-gray/10 flex items-center justify-between text-xs font-semibold text-brand-orange">
                      <span>Clinical Advantage</span>
                      <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 3. LUXURY OBSIDIAN & AMBER DARK CARDS */}
          {variant === "grid_gradient_cards" && (
            <div className={`my-8 md:my-10 grid grid-cols-1 gap-6 ${gridColClass}`}>
              {section.items.map((item, itemIdx) => {
                const IconComp = ICONS[itemIdx % ICONS.length];
                return (
                  <div
                    key={itemIdx}
                    className="group relative overflow-hidden rounded-3xl border border-white/10 bg-[#0f1115] p-8 text-white shadow-2xl transition-all duration-300 hover:-translate-y-2 hover:border-brand-orange/60 hover:shadow-2xl hover:shadow-brand-orange/20 flex flex-col justify-between"
                  >
                    {/* Amber ambient radial background */}
                    <div className="absolute top-0 right-0 h-36 w-36 bg-gradient-to-bl from-brand-orange/20 to-transparent rounded-full blur-2xl group-hover:from-brand-orange/35 transition-all pointer-events-none" />
                    
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-orange to-brand-orange-deep text-white shadow-lg shadow-brand-orange/30">
                          <IconComp className="h-6 w-6" />
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-brand-orange border border-brand-orange/30 px-2.5 py-1 rounded-full bg-brand-orange/10">
                          Pillar 0{itemIdx + 1}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2.5 group-hover:text-brand-orange transition-colors">
                        {item.title}
                      </h3>
                      {item.description && (
                        <div
                          className="text-sm text-white/75 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      )}
                    </div>

                    <div className="relative z-10 mt-6 pt-4 border-t border-white/10 flex items-center gap-2 text-xs font-semibold text-brand-orange">
                      <Sparkles className="h-3.5 w-3.5" />
                      <span>Certified Clinical Outcome</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 4. INTERACTIVE SWIPER CAROUSEL VARIANT */}
          {variant === "grid_carousel" && (
            <div 
              ref={scrollContainerRef}
              onScroll={checkScroll}
              className="my-8 flex gap-6 overflow-x-auto pb-6 pt-2 no-scrollbar [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] snap-x snap-mandatory"
            >
              {section.items.map((item, itemIdx) => {
                const IconComp = ICONS[itemIdx % ICONS.length];
                return (
                  <div
                    key={itemIdx}
                    className="min-w-[300px] sm:min-w-[360px] max-w-[420px] shrink-0 snap-start rounded-3xl border border-brand-gray/15 bg-white p-8 shadow-md transition-all duration-300 hover:-translate-y-1.5 hover:border-brand-orange/50 hover:shadow-2xl flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex h-13 w-13 items-center justify-center rounded-2xl bg-brand-peach/40 text-brand-orange">
                          <IconComp className="h-6 w-6" />
                        </div>
                        <span className="font-mono text-xs font-bold text-brand-orange-deep bg-brand-orange/10 px-3 py-1 rounded-full">
                          0{itemIdx + 1}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-brand-black mb-3">
                        {item.title}
                      </h3>
                      {item.description && (
                        <div 
                          className="text-sm sm:text-base text-brand-gray leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 5. NUMBERED EDITORIAL PILLAR BOXES */}
          {variant === "grid_boxed" && (
            <div className={`my-8 md:my-10 grid grid-cols-1 gap-6 ${gridColClass}`}>
              {section.items.map((item, itemIdx) => {
                return (
                  <div
                    key={itemIdx}
                    className="group relative overflow-hidden rounded-3xl border border-brand-gray/20 bg-white p-8 transition-all duration-300 hover:border-brand-orange hover:shadow-xl flex flex-col justify-between"
                  >
                    {/* Watermark Ghost Number */}
                    <span className="absolute -right-2 -bottom-4 text-7xl sm:text-8xl font-black text-brand-gray-light/60 select-none group-hover:text-brand-orange/10 transition-colors pointer-events-none font-mono">
                      0{itemIdx + 1}
                    </span>

                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="h-2 w-2 rounded-full bg-brand-orange" />
                        <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-deep">
                          Protocol 0{itemIdx + 1}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-brand-black mb-3 group-hover:text-brand-orange-deep transition-colors">
                        {item.title}
                      </h3>
                      {item.description && (
                        <div 
                          className="text-sm sm:text-base text-brand-gray leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      )}
                    </div>

                    <div className="relative z-10 mt-6 h-1 w-12 rounded-full bg-brand-orange/30 group-hover:w-full group-hover:bg-brand-orange transition-all duration-500" />
                  </div>
                );
              })}
            </div>
          )}

          {/* 6. HIGH-DENSITY FEATURE CHECKLIST */}
          {variant === "grid_compact_list" && (
            <div className="my-8 md:my-10 grid grid-cols-1 md:grid-cols-2 gap-4">
              {section.items.map((item, itemIdx) => (
                <div
                  key={itemIdx}
                  className="flex items-start gap-4 rounded-2xl border border-brand-gray/15 bg-white p-5 shadow-2xs transition-all hover:border-brand-orange/50 hover:bg-brand-peach/10"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-orange to-brand-orange-deep text-white shadow-xs">
                    <Check className="h-5 w-5 stroke-[2.5]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-bold text-brand-black">
                      {item.title}
                    </h3>
                    {item.description && (
                      <div 
                        className="mt-1 text-sm text-brand-gray leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: item.description }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 7. FLOATING MODERN CARDS (DEFAULT) */}
          {variant === "grid_modern" && (
            <div className={`my-8 md:my-10 grid grid-cols-1 gap-6 ${gridColClass}`}>
              {section.items.map((item, itemIdx) => {
                const IconComp = ICONS[itemIdx % ICONS.length];
                return (
                  <div
                    key={itemIdx}
                    className="group relative flex flex-col justify-between rounded-3xl border border-brand-gray/15 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-brand-orange/40 hover:shadow-xl"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-peach/40 text-brand-orange group-hover:bg-brand-orange group-hover:text-white transition-colors duration-300 shadow-xs">
                          <IconComp className="h-6 w-6" />
                        </div>
                        <span className="text-xs font-bold text-brand-gray bg-brand-gray-light px-2.5 py-1 rounded-full">
                          0{itemIdx + 1}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-brand-black mb-2.5 group-hover:text-brand-orange-deep transition-colors">
                        {item.title}
                      </h3>
                      {item.description && (
                        <div 
                          className="text-sm sm:text-base text-brand-gray leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
